// ToolsHubPage — публичен hub на всички безплатни инструменти.
// Групиране по тип: скрининг, хранене, лаборатория, обучение.

import React, { Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { LanguageSwitcher } from "@/components/SubpageLayout";
import {
  Camera,
  Activity,
  Scan,
  Flower2,
  FlaskConical,
  Brain,
  Utensils,
  HeartPulse,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  BookOpen,
  Baby,
} from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import SEO from "@/components/SEO";
import "@/styles/landing-brand.css";

// Lazy-load the heavy interactive demos from the landing page bundle.
const LandingNutritionQuiz = lazy(() =>
  import("@/components/LandingNutritionQuiz")
);
const LandingInsulinFoodGallery = lazy(() =>
  import("@/components/LandingInsulinFoodGallery")
);
const LandingMealAI = lazy(() => import("@/components/LandingMealAI"));
const LandingPersonalInsulinLoad = lazy(() =>
  import("@/components/LandingPersonalInsulinLoad")
);

const buildTools = (L) => [
  {
    group: L("Скрининг и ранно откриване", "Screening and early detection", "Cribado y detección temprana"),
    color: "#e44288",
    items: [
      {
        icon: Camera,
        title: L("Фото скенер за кожа", "Skin photo scanner", "Escáner fotográfico de piel"),
        body: L(
          "Качете снимка на бенка или кожна промяна. Платформата разчита формата, ръбовете и цвета и дава образователна оценка.",
          "Upload a photo of a mole or skin change. The platform reads the shape, edges and colour and gives an educational assessment.",
          "Suba una foto de un lunar o una lesión cutánea. La plataforma analiza su forma, bordes y color y ofrece una evaluación educativa."
        ),
        link: "/skin-screening",
        testid: "tool-skin-screening",
      },
      {
        icon: HeartPulse,
        title: L("Програма за здраве на гърдата", "Breast Health Programme", "Programa de salud mamaria"),
        body: L(
          "Над 29 раздела: метаболитна оценка от 0 до 100, наследствен риск, плътност на тъканта, биологична възраст на гърдата, личен план за превенция и клинична справка за Вашия лекар.",
          "Over 29 sections: a 0–100 metabolic score, hereditary risk, tissue density, biological age of the breast, a personal prevention plan and a clinical summary for your doctor.",
          "Más de 29 secciones: puntuación metabólica de 0 a 100, riesgo hereditario, densidad del tejido, edad biológica de la mama, plan personal de prevención e informe clínico para su médico."
        ),
        link: "/health-intelligence/breast",
        testid: "tool-breast-health",
      },
    ],
  },
  {
    group: L("Хранене и метаболизъм", "Nutrition and metabolism", "Nutrición y metabolismo"),
    color: "#287a2e",
    items: [
      {
        icon: Camera,
        title: L("Разпознаване на храна по снимка", "Meal recognition from a photo", "Reconocimiento de comidas por foto"),
        body: L(
          "Снимайте ястие — AI разпознава съставките, порциите и изчислява метаболитния ефект.",
          "Snap a meal — the AI recognises the ingredients and portions and calculates the metabolic effect.",
          "Fotografíe un plato — la IA reconoce los ingredientes y las porciones y calcula el efecto metabólico."
        ),
        link: "/tools/scanner",
        testid: "tool-photo-scanner",
      },
      {
        icon: Activity,
        title: L("Инсулинов товар", "Insulin load", "Carga insulínica"),
        body: L(
          "Проследете хранителния инсулинов товар и индивидуалния инсулинов индекс на всяко ястие — не само калориите.",
          "Track the insulin load of your food and the individual insulin index of every meal — not just calories.",
          "Siga la carga insulínica de la comida y el índice insulínico individual de cada plato — no solo las calorías."
        ),
        link: "/platform",
        testid: "tool-dil-audit",
      },
      {
        icon: Utensils,
        title: L("Симулатор на чинията", "Plate simulator", "Simulador del plato"),
        body: L(
          "Проверете предварително ефекта на хранене — свързано с мрежата от дигитални близнаци.",
          "Check a meal's effect in advance — connected to the digital-twin network.",
          "Compruebe el efecto de una comida de antemano — conectado a la red de gemelos digitales."
        ),
        link: "/platform",
        testid: "tool-plate-simulator",
      },
    ],
  },
  {
    group: L("Лабораторни изследвания", "Lab tests", "Análisis de laboratorio"),
    color: "#5e227f",
    items: [
      {
        icon: FlaskConical,
        title: L("Покритие на лаборатории", "Lab coverage", "Cobertura de laboratorios"),
        body: L(
          "Вижте кои лаборатории покриваме и как се интерпретират Вашите изследвания.",
          "See which labs we cover and how your results are interpreted.",
          "Vea qué laboratorios cubrimos y cómo se interpretan sus análisis."
        ),
        link: "/labs",
        testid: "tool-lab-coverage",
      },
      {
        icon: Brain,
        title: L("Хранителен тест", "Nutrition quiz", "Cuestionario nutricional"),
        body: L(
          "Оценка на хранителните Ви навици и рискови фактори за 3 минути.",
          "An assessment of your eating habits and risk factors in 3 minutes.",
          "Una evaluación de sus hábitos alimentarios y factores de riesgo en 3 minutos."
        ),
        link: "/assessment",
        testid: "tool-nutrition-quiz",
      },
    ],
  },
  {
    group: L("Обучение и наука", "Education and science", "Educación y ciencia"),
    color: "#9c39a1",
    items: [
      {
        icon: Flower2,
        title: L("Индекс за колаген", "Collagen index", "Índice de colágeno"),
        body: L(
          "Персонализиран индекс на колагенови нужди според възраст, активност и показатели.",
          "A personalised collagen-needs index based on your age, activity and markers.",
          "Un índice personalizado de necesidades de colágeno según su edad, actividad y marcadores."
        ),
        link: "/tools/collagen",
        testid: "tool-collagen-index",
      },
      {
        icon: BookOpen,
        title: L("Научна база", "Knowledge library", "Biblioteca de conocimiento"),
        body: L(
          "Рецензираните научни публикации, върху които стъпва платформата.",
          "The peer-reviewed scientific publications the platform is built on.",
          "Las publicaciones científicas revisadas por pares en las que se apoya la plataforma."
        ),
        link: "/knowledge-library",
        testid: "tool-knowledge-library",
      },
      {
        icon: Sparkles,
        title: L("Симптом асистент", "Symptom assistant", "Asistente de síntomas"),
        body: L(
          "Опишете симптом — асистентът Ви насочва към възможни физиологични системи за проверка.",
          "Describe a symptom — the assistant points you to the physiological systems worth checking.",
          "Describa un síntoma — el asistente le indica los sistemas fisiológicos que conviene revisar."
        ),
        link: "/assessment",
        testid: "tool-symptom-assessment",
      },
    ],
  },
];

export default function ToolsHubPage() {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const L = (bgT, enT, esT) => (language === "bg" ? bgT : language === "es" ? (esT ?? enT) : enT);
  const TOOLS = buildTools(L);
  return (
    <div
      data-testid="tools-hub-root"
      className={`min-h-screen ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-white"}`}
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <SEO
        title={L("Инструменти — Nutramilo", "Tools — Nutramilo", "Herramientas — Nutramilo")}
        description={L(
          "Безплатни скриниращи и образователни инструменти: фото скенер за кожа, стоматологичен скрининг, инсулинов товар, симулатор на чинията и още.",
          "Free screening and educational tools: skin photo scanner, dental screening, insulin load, plate simulator and more.",
          "Herramientas gratuitas de cribado y educativas: escáner de piel, cribado dental, carga insulínica, simulador del plato y más."
        )}
      />

      {/* Sticky back nav */}
      <nav
        data-testid="tools-nav"
        className={`sticky top-0 z-30 backdrop-blur-xl border-b ${
          isDark
            ? "bg-[hsl(223,47%,6%)]/90 border-white/10"
            : "bg-white/90 border-slate-100"
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link
            to="/"
            data-testid="tools-nav-home"
            className={`inline-flex items-center gap-2 text-sm font-semibold ${
              isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-[#287a2e]"
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            {L("Към Nutramilo", "Back to Nutramilo", "Volver a Nutramilo")}
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/scientific-formulas"
              data-testid="tools-nav-science"
              className={`text-xs font-medium ${
                isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"
              }`}
            >
              {L("Методика", "Methodology", "Metodología")}
            </Link>
            <LanguageSwitcher isDark={isDark} testIdPrefix="tools-lang" />
            <Link
              to="/pricing"
              data-testid="tools-nav-pricing"
              className={`text-xs font-medium ${
                isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"
              }`}
            >
              {L("Цени", "Pricing", "Precios")}
            </Link>
            <Link
              to="/register"
              data-testid="tools-nav-cta"
              className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold px-4 py-2 rounded-full text-white brand-cta-primary"
            >
              {L("Безплатно", "Free", "Gratis")}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section
        className={`relative overflow-hidden py-16 sm:py-20 border-b ${
          isDark
            ? "bg-[hsl(223,47%,8%)] border-white/10"
            : "bg-[#F9F9F7] border-slate-100"
        }`}
      >
        <div
          className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, #9c39a1 0%, #e44288 50%, transparent 70%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1
            data-testid="tools-hub-title"
            className={`text-4xl sm:text-5xl font-bold tracking-tight mb-4 ${
              isDark ? "text-white" : "text-slate-900"
            }`}
          >
            {L("Инструменти", "Tools", "Herramientas")}
          </h1>
          <p
            className={`text-lg max-w-2xl ${
              isDark ? "text-slate-400" : "text-slate-600"
            }`}
          >
            {L(
              "Безплатни образователни и скриниращи модули. Използвайте ги самостоятелно или ги комбинирайте с профила си в Nutramilo за персонализирана обратна връзка.",
              "Free educational and screening modules. Use them on their own or combine them with your Nutramilo account for personalised feedback.",
              "Módulos educativos y de cribado gratuitos. Úselos de forma independiente o combínelos con su cuenta de Nutramilo para recibir una respuesta personalizada."
            )}
          </p>
        </div>
      </section>

      {/* Groups */}
      {TOOLS.map((grp, gi) => (
        <section
          key={grp.group}
          data-testid={`tools-group-${gi}`}
          className={`py-14 sm:py-16 ${
            gi % 2 === 0
              ? isDark
                ? "bg-[hsl(223,47%,7%)]"
                : "bg-white"
              : isDark
              ? "bg-[hsl(223,47%,8%)]"
              : "bg-[#F9F9F7]"
          }`}
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <span
                className="w-10 h-1 rounded-full"
                style={{ background: grp.color }}
              />
              <h2
                className={`text-xs font-bold uppercase tracking-wider ${
                  isDark ? "text-slate-300" : "text-slate-600"
                }`}
                style={{ color: grp.color }}
              >
                {grp.group}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {grp.items.map((t, i) => {
                const Icon = t.icon;
                return (
                  <Link
                    key={i}
                    to={t.link}
                    data-testid={t.testid}
                    className={`group relative p-6 rounded-2xl transition-all hover:-translate-y-1 ${
                      isDark
                        ? "bg-[hsl(223,47%,10%)] border border-white/10 hover:border-white/20"
                        : "bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md"
                    }`}
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                      style={{
                        background: `linear-gradient(135deg, ${grp.color}25, ${grp.color}10)`,
                      }}
                    >
                      <Icon className="w-5 h-5" style={{ color: grp.color }} />
                    </div>
                    <h3
                      className={`text-base font-semibold mb-2 flex items-center gap-2 ${
                        isDark ? "text-white" : "text-slate-900"
                      }`}
                    >
                      {t.title}
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                    </h3>
                    <p
                      className={`text-sm leading-relaxed ${
                        isDark ? "text-slate-400" : "text-slate-600"
                      }`}
                    >
                      {t.body}
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* Interactive live demos (moved from LandingPage) */}
      <section
        className={`py-16 border-t ${
          isDark
            ? "bg-[hsl(223,47%,6%)] border-white/10"
            : "bg-[#F9F9F7] border-slate-100"
        }`}
        data-testid="tools-interactive"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
          <div
            className="inline-block text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: "#e44288" }}
          >
            {L("Опитайте веднага", "Try it now", "Pruébelo ahora")}
          </div>
          <h2
            className={`text-3xl sm:text-4xl font-bold tracking-tight mb-3 ${
              isDark ? "text-white" : "text-slate-900"
            }`}
          >
            {L("Живи демонстрации", "Live demonstrations", "Demostraciones en vivo")}
          </h2>
          <p
            className={`text-base max-w-3xl ${
              isDark ? "text-slate-400" : "text-slate-600"
            }`}
          >
            {L(
              "Четири интерактивни модула — тест, скрит инсулин, симулатор на чинията и личен инсулинов товар. Без регистрация.",
              "Four interactive modules — a quiz, hidden insulin, a plate simulator and personal insulin load. No sign-up.",
              "Cuatro módulos interactivos — un cuestionario, insulina oculta, un simulador del plato y carga insulínica personal. Sin registro."
            )}
          </p>
        </div>

        <Suspense
          fallback={
            <div
              className={`text-center py-10 text-sm ${
                isDark ? "text-slate-400" : "text-slate-500"
              }`}
              data-testid="tools-interactive-loading"
            >
              {L("Зареждаме модулите…", "Loading the modules…", "Cargando los módulos…")}
            </div>
          }
        >
          <div data-testid="tools-interactive-quiz">
            <LandingNutritionQuiz />
          </div>
          <div data-testid="tools-interactive-food-gallery">
            <LandingInsulinFoodGallery />
          </div>
          <div data-testid="tools-interactive-meal-ai">
            <LandingMealAI />
          </div>
          <div data-testid="tools-interactive-personal-insulin">
            <LandingPersonalInsulinLoad />
          </div>
        </Suspense>
      </section>

      {/* CTA */}
      <section
        className={`py-16 border-t ${
          isDark
            ? "bg-[hsl(223,47%,6%)] border-white/10"
            : "bg-white border-slate-100"
        }`}
      >
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h3
            className={`text-2xl sm:text-3xl font-bold mb-3 ${
              isDark ? "text-white" : "text-slate-900"
            }`}
          >
            {L("Искате всичко на едно място?", "Want it all in one place?", "¿Lo quiere todo en un solo lugar?")}
          </h3>
          <p
            className={`text-base mb-6 ${
              isDark ? "text-slate-400" : "text-slate-600"
            }`}
          >
            {L(
              "С безплатен профил получавате персонализирана обратна връзка от всеки инструмент и достъп до мрежата от дигитални близнаци.",
              "A free account gives you personalised feedback from every tool and access to the digital-twin network.",
              "Con una cuenta gratuita obtiene una respuesta personalizada de cada herramienta y acceso a la red de gemelos digitales."
            )}
          </p>
          <Link to="/register">
            <button
              data-testid="tools-hub-cta-register"
              className="brand-cta-primary rounded-full px-7 py-3 text-sm font-semibold"
            >
              {L("Започнете безплатно", "Start free", "Empezar gratis")}
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
